// Hotel Data Context - Provides hotel data from MongoDB to all components
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import * as api from '../services/api';
import { useLanguage } from './LanguageContext';

// Room name/description/amenities in the visitor's language (English base fields stay as fallback).
export const localizeRoom = (room, lang) => {
  const tr = lang && lang !== 'en' ? room.translations?.[lang] : null;
  if (!tr || !tr.name) return room;
  return { ...room, name: tr.name, description: tr.description || room.description, amenities: tr.amenities || room.amenities, name_en: room.name };
};

// Import mock data as fallback
import {
  hotelInfo as mockHotelInfo,
  rooms as mockRooms,
  gallery as mockGallery,
  amenities as mockAmenities,
  services as mockServices,
  testimonials as mockTestimonials,
  faqs as mockFaqs,
  nearbyAttractions as mockNearbyAttractions,
} from '../data/mock';

const HotelContext = createContext(null);

export function HotelProvider({ children }) {
  const [hotelInfo, setHotelInfo] = useState(mockHotelInfo);
  const [rawRooms, setRooms] = useState(mockRooms);
  const { lang } = useLanguage();
  const rooms = useMemo(() => rawRooms.map((r) => localizeRoom(r, lang)), [rawRooms, lang]);
  const [gallery, setGallery] = useState(mockGallery);
  const [amenities, setAmenities] = useState(mockAmenities);
  const [services, setServices] = useState(mockServices);
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [faqs, setFaqs] = useState(mockFaqs);
  const [rawAttractions, setNearbyAttractions] = useState(mockNearbyAttractions);
  const nearbyAttractions = useMemo(() => rawAttractions.map((a) => { const tr = lang !== 'en' && a.translations?.[lang]; return tr?.name ? { ...a, name: tr.name, description: tr.description || a.description } : a; }), [rawAttractions, lang]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [
          hotelInfoData,
          roomsData,
          galleryData,
          amenitiesData,
          servicesData,
          testimonialsData,
          faqsData,
          attractionsData,
        ] = await Promise.all([
          api.getHotelInfo().catch(() => mockHotelInfo),
          api.getRooms().catch(() => mockRooms),
          api.getGallery().catch(() => mockGallery),
          api.getAmenities().catch(() => mockAmenities),
          api.getServices().catch(() => mockServices),
          api.getTestimonials().catch(() => mockTestimonials),
          api.getFaqs().catch(() => mockFaqs),
          api.getNearbyAttractions().catch(() => mockNearbyAttractions),
        ]);

        setHotelInfo(hotelInfoData);
        setRooms(roomsData);
        setGallery(galleryData);
        setAmenities(amenitiesData);
        setServices(servicesData);
        setTestimonials(testimonialsData);
        setFaqs(faqsData);
        setNearbyAttractions(attractionsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching hotel data:', err);
        setError(err.message);
        // Keep using mock data as fallback
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  // Function to get a room by ID
  const getRoomById = (id) => {
    return rooms.find(room => room.id === parseInt(id));
  };

  // Function to refresh data
  const refreshData = async () => {
    api.clearCache();
    setLoading(true);
    try {
      const [roomsData, galleryData] = await Promise.all([
        api.getRooms(),
        api.getGallery(),
      ]);
      setRooms(roomsData);
      setGallery(galleryData);
    } catch (err) {
      console.error('Error refreshing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    hotelInfo,
    rooms,
    gallery,
    amenities,
    services,
    testimonials,
    faqs,
    nearbyAttractions,
    loading,
    error,
    getRoomById,
    refreshData,
  };

  return (
    <HotelContext.Provider value={value}>
      {children}
    </HotelContext.Provider>
  );
}

export function useHotel() {
  const context = useContext(HotelContext);
  if (!context) {
    throw new Error('useHotel must be used within a HotelProvider');
  }
  return context;
}

export default HotelContext;
